import { useState } from "react";
import { Link } from "react-router-dom";
import { Bell, CheckCheck } from "lucide-react";
import { useNotifications } from "@/hooks/useNotifications";
import { usePushRegistration } from "@/hooks/usePushRegistration";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { formatDate } from "@/lib/format";

export default function NotificationsBell() {
  const { user } = useAuth();
  const { items, unread, markRead, markAllRead } = useNotifications(user);
  const [open, setOpen] = useState(false);
  usePushRegistration({
    swPath: "/staff-sw.js",
    publicKeyUrl: "/api/push/public-key",
    subscribeUrl: "/push/subscribe",
    post: (url, body) => api.post(url, body),
    enabled: !!user,
  });
  if (!user) return null;

  const request = async () => {
    if (typeof Notification !== "undefined" && Notification.permission === "default") {
      try { await Notification.requestPermission(); } catch {}
    }
    setOpen((o) => !o);
  };

  return (
    <div className="relative" data-testid="notifications-bell">
      <button onClick={request} className="relative p-2 rounded-full hover:bg-royal-700/20 text-cream/90" aria-label="Notifications">
        <Bell size={18} />
        {unread > 0 && (
          <span data-testid="notif-unread-badge" className="absolute -top-0.5 -right-0.5 bg-gold text-ink rounded-full text-[10px] font-mono-tight font-semibold min-w-[18px] h-[18px] flex items-center justify-center px-1">{unread > 99 ? "99+" : unread}</span>
        )}
      </button>
      {open && (
        <>
          <div className="fixed inset-0 z-40" onClick={() => setOpen(false)} />
          <div className="absolute right-0 top-full mt-2 w-80 md:w-96 bg-white rounded-2xl border border-black/10 shadow-2xl z-50 overflow-hidden" data-testid="notif-dropdown">
            <div className="px-4 py-3 border-b border-black/5 flex items-center justify-between">
              <div className="label-caps">Notifications</div>
              {items.length > 0 && (
                <button data-testid="notif-mark-all" onClick={markAllRead} className="text-xs inline-flex items-center gap-1 underline underline-offset-4"><CheckCheck size={12} /> Mark all read</button>
              )}
            </div>
            <div className="max-h-96 overflow-auto">
              {items.length === 0 && <div className="p-6 text-center text-sm text-ink/60">You're all caught up.</div>}
              {items.map((n) => (
                <Link key={n.id} to={n.link || "#"} onClick={() => { markRead(n.id); setOpen(false); }}
                  data-testid={`notif-item-${n.id}`}
                  className={`block px-4 py-3 border-b border-black/5 hover:bg-cream ${n.read ? "" : "bg-cream-2/60"}`}>
                  <div className="flex justify-between items-baseline gap-2">
                    <div className="font-semibold text-sm">{n.title}</div>
                    {!n.read && <span className="w-2 h-2 rounded-full bg-royal-700 mt-1 shrink-0" />}
                  </div>
                  {n.body && <div className="text-xs text-ink/70 mt-0.5">{n.body}</div>}
                  <div className="text-[10px] text-ink/50 font-mono-tight mt-1">{formatDate(n.created_at)}</div>
                </Link>
              ))}
            </div>
          </div>
        </>
      )}
    </div>
  );
}
